import React, { FC, useEffect, useState, useCallback } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../common/toast/hooks/useToast'
import { Profile, ProfileFormProps } from './ProfileForm.d'
import { Card } from 'primereact/card'
import { api } from '../../../auth'
import Common from '../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../common/forms/fields/TextField'
import ImageField from '../../common/forms/fields/ImageField'
import FormActions from '../../common/forms/FormActions'
import RenderFieldsErrors from '../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../common/forms/errors/Helper'
import {
    Language,
    setLoggedInUser,
} from '../../../features/security/security.slice'
import { getGroupTranslation } from '../../common/utility/helpers'
import RenderTranslatedFields from '../../common/forms/fields/translated-fields/RenderTranslatedFields'
import { useAuth } from '../../../auth'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../common/forms/errors/RenderFormikErrors'
import { HintMessage } from '../../common/messages/Messages'
import ImageReccomendedDimentionsMessage from '../../common/messages/ImageReccomendedDimentionsMessage'

const ProfileForm: FC<ProfileFormProps> = ({
    inPopup = false,
    config = {},
}) => {
    const initData = useAppSelector((state: any) => state.security.initData)
    const theme = useAppSelector((state) => state.theme.theme)
    const { user } = useAuth()

    const [pageCode, setPageCode] = useState('TMPSET')

    const navigate = useNavigate()
    const axiosPrivate = api
    const dispatch = useAppDispatch()

    const emptyProfile: Profile = {
        email: '',
        first_name: '',
        middle_names: '',
        last_name: '',
        media_store_id: null,
        user_image: null,
        remove_user_image: false,
    }

    const [pageDialog, setPageDialog] = useState(true)
    const [profile, setProfile] = useState<Profile>(emptyProfile)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema: Record<string, any> = {
        email: Yup.string().trim().required('Enter your email address'),
        first_name: Yup.string().trim().required('Required'),
        last_name: Yup.string().trim().required('Required'),
    }

    let _initialValues: Record<string, any> = {
        ...profile,
        first_name: pageCode + '_FIRST_NAME',
        middle_names: pageCode + '_MIDDLE_NAMES',
        last_name: pageCode + '_LAST_NAME',
    }

    ;(Object.values(initData.languages.data) as Language[]).map((lang) => {
        schema[`first_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The First Name (${lang.name}) field is required`)
        schema[`last_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Last Name (${lang.name}) field is required`)
        _initialValues[`first_name_${lang.id}`] = ''
        _initialValues[`last_name_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const ProfileFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/account/profile')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                if (response.data.data.profile) {
                    setPageCode('USER_' + response.data.data.profile.id)
                    setProfile(response.data.data.profile)

                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.profile,
                        }
                    })

                    Object.keys(initData.languages.data).map((locale) => {
                        setProfile((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations: Record<string, any> = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'first_name',
                                        code: response.data.data.profile
                                            .first_name,
                                    },
                                    {
                                        key: 'middle_names',
                                        code: response.data.data.profile
                                            .middle_names,
                                    },
                                    {
                                        key: 'last_name',
                                        code: response.data.data.profile
                                            .last_name,
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`first_name_${lang.id}`]:
                                        groupTranslations['first_name'],
                                    [`middle_names_${lang.id}`]:
                                        groupTranslations['middle_names'],
                                    [`last_name_${lang.id}`]:
                                        groupTranslations['last_name'],
                                }
                            })

                            return {
                                ...prevState,
                                [`first_name_${lang.id}`]:
                                    groupTranslations['first_name'],
                                [`middle_names_${lang.id}`]:
                                    groupTranslations['middle_names'],
                                [`last_name_${lang.id}`]:
                                    groupTranslations['last_name'],
                            }
                        })

                        return true
                    })
                }

                setAllowRemoveImage(
                    response.data.data.profile.media_store_id !== null
                )
                setPreviewSrc(
                    response.data.data.profile.media_store_id
                        ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.profile.media_store_id}`
                        : ''
                )
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('User record was not found')
            } else {
                setErrMsg('Failed to load profile')
            }
        }
    }, [axiosPrivate, initData.languages.data])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        record.remove_user_image =
            !record.user_image &&
            record.media_store_id !== null &&
            !allowRemoveImage

        try {
            const response = await axiosPrivate
                .post('/account/profile/update', record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                dispatch(setLoggedInUser(response.data?.data?.user))
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setPageDialog(false)
        navigate('/account')
    }

    const FormFooter = (
        <React.Fragment>
            {inPopup ? (
                <Button
                    variant={theme === 'dark' ? 'dark' : 'light'}
                    onClick={hideDialog}
                >
                    <span className="small">Cancel</span>
                </Button>
            ) : null}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'profile-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={ProfileFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="update-my-profile-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col lg={9}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <RenderTranslatedFields
                                                    id="userProfile"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'formProfileFirstName',
                                                            type: 'text',
                                                            name: 'first_name',
                                                            label: 'First Name',
                                                            placeholder:
                                                                'Enter first name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formProfileMiddleNames',
                                                            type: 'text',
                                                            name: 'middle_names',
                                                            label: 'Middle Name(s)',
                                                            placeholder:
                                                                'Enter middle name(s)',
                                                        },
                                                        {
                                                            id: 'formProfileLastName',
                                                            type: 'text',
                                                            name: 'last_name',
                                                            label: 'Last Name',
                                                            placeholder:
                                                                'Enter last name',
                                                            required: true,
                                                        },
                                                    ]}
                                                />
                                            </Col>
                                            <Col xl={6}>
                                                <Row>
                                                    <Col xl={12}>
                                                        <Form.Group
                                                            className="sfy-field mb-3"
                                                            controlId="formUsernameReadonly"
                                                        >
                                                            <Form.Label>
                                                                Username
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={
                                                                    user?.username ?? ''
                                                                }
                                                                className=" readonly"
                                                                readOnly={true}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xl={12}>
                                                        <TextField
                                                            id="formProfileEmail"
                                                            name="email"
                                                            label="Email"
                                                            placeholder="Enter email"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </fieldset>

                                    <fieldset>
                                        <legend>Change Password</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formProfileCurrentPassword"
                                                    name="current_password"
                                                    label="Current Password"
                                                    placeholder="Enter current password"
                                                    formikProps={formikProps}
                                                    type="password"
                                                />
                                            </Col>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formProfileNewPassword"
                                                    name="new_password"
                                                    label="New Password"
                                                    placeholder="Enter new password"
                                                    formikProps={formikProps}
                                                    type="password"
                                                />
                                                <TextField
                                                    id="formProfileConfirmPassword"
                                                    name="confirm_password"
                                                    label="Confirm Password"
                                                    placeholder="Enter new password again"
                                                    formikProps={formikProps}
                                                    type="password"
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                                <Col lg={3}>
                                    <fieldset>
                                        <legend>
                                            Image{' '}
                                            <HintMessage
                                                message={
                                                    <ImageReccomendedDimentionsMessage size="1024 x 1024 pixles" />
                                                }
                                            />
                                        </legend>
                                        <ImageField
                                            id="profileUserImageFile"
                                            name="user_image"
                                            label="Image"
                                            showLabel={false}
                                            formikProps={formikProps}
                                            imageId={profile.media_store_id}
                                            alt={`${profile.first_name} image`}
                                            allowRemoveImage={allowRemoveImage}
                                            setAllowRemoveImage={
                                                setAllowRemoveImage
                                            }
                                            previewSrc={previewSrc}
                                            setPreviewSrc={setPreviewSrc}
                                            updateRecord={setProfile}
                                        />
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="profile-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={pageDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '60vw' }}
                        header={'Edit my profile'}
                        modal
                        className="p-fluid"
                        footer={FormFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : undefined}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    {FormContent}
                    <FormActions formFooter={FormFooter} />
                </>
            )}
        </>
    )
}

export default ProfileForm
